import { useEffect, useState } from "react";
import { ShoppingCart, ArrowRight, CheckCircle2, Clock, XCircle, Package } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import FoldBlock from "./FoldBlock";

const STATUS_META = {
  pending_review: { Icon: Clock, color: "#F59E0B", label: "Pending review" },
  quoted: { Icon: ArrowRight, color: "#A78BFA", label: "Quoted" },
  confirmed: { Icon: CheckCircle2, color: "#1FB6C1", label: "Confirmed" },
  fulfilled: { Icon: CheckCircle2, color: "#10B981", label: "Fulfilled" },
  cancelled: { Icon: XCircle, color: "#F43F5E", label: "Cancelled" },
};

export default function OrdersBlock({ customerId, defaultOpen = false }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const { data } = await api.get(`/order-requests?customer_id=${customerId}`);
        if (!abort) setOrders(data || []);
      } catch (_e) {
        if (!abort) setOrders([]);
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [customerId]);

  const sorted = [...orders].sort((a, b) =>
    String(b.created_at || "").localeCompare(String(a.created_at || "")),
  );

  return (
    <FoldBlock
      icon={ShoppingCart}
      iconColor="#F59E0B"
      title="Orders"
      count={orders.length}
      defaultOpen={defaultOpen}
      navigateTo="/order-requests"
      navigateLabel="All orders"
      testId="block-orders"
      emptyHint={loading ? "Loading orders…" : "No orders yet for this customer."}
    >
      <div className="divide-y divide-white/5">
        {sorted.map((o) => {
          const meta = STATUS_META[o.status] || { Icon: Package, color: "#647087", label: o.status };
          const lineCount = (o.lines || []).length;
          return (
            <Link
              key={o.id}
              to={`/order-requests?focus=${o.id}`}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.03] transition-colors"
              data-testid={`order-row-${o.id}`}
            >
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 border"
                style={{
                  borderColor: `${meta.color}55`,
                  background: `${meta.color}1a`,
                  color: meta.color,
                }}
              >
                <meta.Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {lineCount} item{lineCount === 1 ? "" : "s"}
                  {o.needed_by && (
                    <span className="text-[10px] text-[#647087] font-mono ml-2">
                      Needed by {new Date(o.needed_by).toLocaleDateString("en-GB")}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#647087] font-mono">
                  {o.created_at ? new Date(o.created_at).toLocaleDateString("en-GB") : ""}
                  {o.submitted_by_name && <span> · by {o.submitted_by_name}</span>}
                </div>
              </div>
              <span
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border whitespace-nowrap shrink-0"
                style={{
                  borderColor: `${meta.color}55`,
                  background: `${meta.color}10`,
                  color: meta.color,
                }}
              >
                {meta.label}
              </span>
            </Link>
          );
        })}
      </div>
    </FoldBlock>
  );
}
